import { TuiTable } from '@taiga-ui/addon-table';
import { TuiAccordion } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TuiExpand, TuiLoader } from '@taiga-ui/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepPermission } from '@outkeep/shared';
import { AlternativeRequestModel } from '@core/models';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { IconButtonComponent } from '@common/components';
import { firstValueFrom } from 'rxjs';
import { hexToRgba } from '@core/helpers';
import { AlternativeRequestsWidgetDesktopRowComponent } from '../alternative-requests-widget-desktop-row/alternative-requests-widget-desktop-row.component';
import { AlternativeRequestsWidgetMobileCardComponent } from '../alternative-requests-widget-mobile-card/alternative-requests-widget-mobile-card.component';

@Component({
  selector: 'x-alternative-requests-widget',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    TuiTable,
    TuiAccordion,
    TuiExpand,
    TuiLoader,
    TranslatePipe,
    IconButtonComponent,
    AlternativeRequestsWidgetDesktopRowComponent,
    AlternativeRequestsWidgetMobileCardComponent,
  ],
  templateUrl: './alternative-requests-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlternativeRequestsWidgetComponent {
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly permissionService = inject(OutkeepPermissionService);
  protected readonly hexToRgba = hexToRgba;
  private readonly canViewMyRequests = this.permissionService.$hasPermission(
    OutkeepPermission.LogistViewMyRequests,
  );
  private hasStartedInitialLoad = false;

  readonly $isMobile = input(false, { alias: 'isMobile' });
  readonly $requests = signal<AlternativeRequestModel[]>([]);
  readonly $loading = signal(false);
  readonly $loaded = signal(false);
  readonly $initialLoadFailed = signal(false);
  readonly $expanded = signal(true);
  readonly $shouldRender = computed(
    () =>
      this.$initialLoadFailed() ||
      (this.$loaded() && this.$requests().length > 0),
  );

  protected readonly tableColumns: { key: string; minWidth?: string }[] = [
    { key: 'columns_filters.request_number' },
    { key: 'columns_filters.releaseDatePeriod', minWidth: '150px' },
    { key: 'columns_filters.contract' },
    { key: 'columns_filters.car', minWidth: '170px' },
    { key: 'columns_filters.car_plant_number' },
    { key: 'columns_filters.vin' },
    { key: 'columns_filters.project_number' },
    { key: 'columns_filters.request_product', minWidth: '125px' },
    { key: 'columns_filters.subj_name' },
    { key: 'columns_filters.delivery_address' },
    { key: 'columns_filters.select_installer' },
    { key: 'columns_filters.request_status' },
    { key: 'columns_filters.created_at', minWidth: '125px' },
  ];

  constructor() {
    effect(() => {
      if (this.canViewMyRequests() && !this.hasStartedInitialLoad) {
        this.hasStartedInitialLoad = true;
        void this.loadRequests();
      }
    });
  }

  async refreshRequests(): Promise<void> {
    await this.loadRequests();
  }

  toggleExpanded(): void {
    this.$expanded.update(value => !value);
  }

  private async loadRequests(): Promise<void> {
    if (this.$loading()) {
      return;
    }

    this.$loading.set(true);

    try {
      const requests = await firstValueFrom(
        this.requestsApiService.getAlternativeRequests$(),
      );

      this.$requests.set(
        requests.map(request => new AlternativeRequestModel(request)),
      );
      this.$initialLoadFailed.set(false);
      this.$loaded.set(true);
    } catch (error) {
      if (!this.$loaded()) {
        this.$initialLoadFailed.set(true);
      }
      console.error('Failed to load alternative requests', error);
    } finally {
      this.$loading.set(false);
    }
  }
}
